import { createSlice } from '@reduxjs/toolkit'
import { FirestoreDatabase, storageRef } from '../index'
import { Collection, speakerStatus } from 'constants/index'
import { signOut } from 'store/auth'
import { toast } from 'react-toastify'
import { chunk, first, omit } from 'lodash'

export const initialState = {
  error: null,
  inProgress: false,
  speakers: [],
  speaker: null,
  duplicateSpeakers: [],
  currentPage: 0,
  pageSize: 10
}

const speakerSlice = createSlice({
  name: 'speaker',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setSpeakers: (state, action) => {
      state.speakers = action.payload
    },
    setSpeaker: (state, action) => {
      state.speaker = action.payload
    },
    setDuplicateSpeakers: (state, action) => {
      state.duplicateSpeakers = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.speakers = []
      state.speakers = null
      state.duplicateSpeakers = []
      state.currentPage = 0
      state.pageSize = 10
    }
  }
})

export const addSpeaker = (payload, conferenceId) => async (dispatch) => {
  try {
    dispatch(speakerSlice.actions.setProgress(true))
    const { email } = payload
    const snapshot = await FirestoreDatabase.collection(Collection.SPEAKERS)
      .where('email', '==', email)
      .where('conferenceId', '==', conferenceId)
      .get()

    const speakerDoc = first(snapshot.docs)
    const speaker = speakerDoc?.exists ? { id: speakerDoc.id, ...speakerDoc.data() } : null

    if (!speaker) {
      const doc = await FirestoreDatabase.collection(Collection.SPEAKERS).add({
        ...payload,
        conferenceId: conferenceId
      })
      return doc.id
    }
    if (speaker && speaker?.isSubject && speaker?.status === speakerStatus.PENDING) {
      const modifiedSpeaker = omit(speaker, 'id')
      await FirestoreDatabase.collection(Collection.SPEAKERS)
        .doc(speaker.id)
        .update({
          ...modifiedSpeaker,
          ...payload,
          status: speakerStatus.APPROVED
        })
      return speaker.id
    } else {
      toast.error('Speaker with this email already exists!')
      return null
    }
  } catch (err) {
    toast.error('Something went wrong while adding the speaker.')
    console.error(err)
  } finally {
    dispatch(speakerSlice.actions.setProgress(false))
  }
}

export const getExistingSpeakerWithEmail = async (email, conferenceId) => {
  const snapshot = await FirestoreDatabase.collection(Collection.SPEAKERS)
    .where('email', '==', email)
    .where('conferenceId', '==', conferenceId)
    .get()

  const speakers = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
  return speakers?.length ? first(speakers) : null
}

export const linkSubjectToSpeaker = async (speakerId, subjectId) => {
  await FirestoreDatabase.collection(Collection.SPEAKERS).doc(speakerId).update({
    subjectId: subjectId
  })
}

export const checkSpeakerExistsInTalks = (speakerId) => async (dispatch, getState) => {
  try {
    dispatch(speakerSlice.actions.setProgress(true))
    const user = getState().auth.user
    const snapshot = await FirestoreDatabase.collection(Collection.TALKS)
      .where('conferenceId', '==', user?.conferenceId)
      .where('speakers', 'array-contains', speakerId)
      .get()

    return !snapshot.empty
  } catch (err) {
    dispatch(speakerSlice.actions.setError(err))
  } finally {
    dispatch(speakerSlice.actions.setProgress(false))
  }
}

export const fetchSpeakerById = (speakerId) => async (dispatch) => {
  try {
    dispatch(speakerSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.SPEAKERS).doc(speakerId).get()
    const e = snapshot.data()
    dispatch(
      speakerSlice.actions.setSpeaker({
        id: snapshot.id,
        ...e
      })
    )
  } catch (err) {
    dispatch(speakerSlice.actions.setError(err))
  } finally {
    dispatch(speakerSlice.actions.setProgress(false))
  }
}

export const updateSpeakerById = (speakerId, payload, conferenceId) => async (dispatch) => {
  try {
    const { email } = payload
    dispatch(speakerSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.SPEAKERS)
      .where('email', '==', email)
      .where('conferenceId', '==', conferenceId)
      .get()

    const matchingSnapshot = snapshot.docs.filter((doc) => doc.id !== speakerId)

    if (!matchingSnapshot.length) {
      await FirestoreDatabase.collection(Collection.SPEAKERS)
        .doc(speakerId)
        .update({
          ...payload
        })
      return speakerId
    } else {
      toast.error('Speaker with this email already exists!')
    }
  } catch (err) {
    dispatch(speakerSlice.actions.setProgress(false))
    throw err
  } finally {
    dispatch(speakerSlice.actions.setProgress(false))
  }
}

export const updateSpeakerProfile = async (speakerId, imageName) => {
  await FirestoreDatabase.collection(Collection.SPEAKERS).doc(speakerId).update({
    imageName: imageName
  })
}

export const deleteSpeakerById = (conferenceId, speakerId) => async (dispatch) => {
  try {
    dispatch(speakerSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.SPEAKERS).doc(speakerId).get()
    const e = snapshot.data()
    if (e.imageName) {
      await storageRef.child(`${conferenceId}/${Collection.SPEAKERS}/${e.imageName}`).delete()
    }
    await FirestoreDatabase.collection(Collection.SPEAKERS).doc(speakerId).delete()
    if (e.subjectId) {
      await FirestoreDatabase.collection(Collection.SUBJECTS).doc(e.subjectId).update({
        speakerId: ''
      })
    }
    return true
  } catch (err) {
    dispatch(speakerSlice.actions.setError(err))
  } finally {
    dispatch(speakerSlice.actions.setProgress(false))
  }
}

export const getSpeakers = (conferenceId, callback) => {
  return FirestoreDatabase.collection(Collection.SPEAKERS)
    .where('conferenceId', '==', conferenceId)
    .onSnapshot((documents) => {
      const speakers = []
      documents.forEach((doc) => {
        speakers.push({
          id: doc.id,
          ...doc.data()
        })
      })
      callback(speakers)
    })
}

export const getSpeakersAssociatedPolls = (conferenceId) => async (dispatch) => {
  try {
    dispatch(speakerSlice.actions.setProgress(true))
    const speakersSnap = await FirestoreDatabase.collection(Collection.SPEAKERS)
      .where('conferenceId', '==', conferenceId)
      .get()
    const speakers = speakersSnap.docs.map((doc) => {
      return { id: doc?.id, ...doc.data() }
    })
    const speakerIdsList = speakers.map((speaker) => speaker?.id)
    const speakersData = []
    if (speakerIdsList.length < 11) {
      const pollsSnap = await FirestoreDatabase.collection(Collection.POLLS)
        .where('speakerId', 'in', speakerIdsList)
        .get()
      pollsSnap.docs.forEach((doc) => {
        speakersData.push({ id: doc?.id, ...doc.data() })
      })
    } else {
      const batchSize = 10
      const batchedSpeakerIds = chunk(speakerIdsList, batchSize)

      for (const batchSpeakerIds of batchedSpeakerIds) {
        const pollsSnap = await FirestoreDatabase.collection(Collection.POLLS)
          .where('speakerId', 'in', batchSpeakerIds)
          .get()
        pollsSnap.docs.forEach((doc) => {
          speakersData.push({ id: doc?.id, ...doc.data() })
        })
      }
    }
    const extractedSpeakerIdsFromPolls = []
    speakersData.forEach((doc) => {
      if (!extractedSpeakerIdsFromPolls.includes(doc?.speakerId)) {
        extractedSpeakerIdsFromPolls.push(doc?.speakerId)
      }
    })
    const speakerAssociatedWithPolls = extractedSpeakerIdsFromPolls.map((id) => {
      return speakers.find((speaker) => speaker?.id === id)
    })
    dispatch(speakerSlice.actions.setSpeakers(speakerAssociatedWithPolls))
    dispatch(speakerSlice.actions.setProgress(false))
  } catch (err) {
    dispatch(speakerSlice.actions.setProgress(false))
  }
}

export const resetSpeaker = () => (dispatch) => {
  dispatch(speakerSlice.actions.setSpeaker(null))
}

export default speakerSlice
